/* global globalThis */
import { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Card, Grid, Typography, useTheme } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import FilteredTable from '../../TrackerTable/FilteredTable';
import { Spinner } from 'react-bootstrap';
import ErrorComponent from './ErrorComponent';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import EnlargedChartModal from '../EnlargedChartModal';
import { useLocation } from 'react-router-dom';
import { getTabColor } from '../../../shared/constants/constants';
import { useQuery } from '@tanstack/react-query';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AnalyticsCardHeader from './AnalyticsCardHeader';
import {
  ANALYTICS_CARD_BORDER,
  ANALYTICS_CARD_BORDER_SOFT,
  ANALYTICS_CARD_SHADOW,
  ANALYTICS_SURFACE,
} from '../analyticsColors';
import InfoPopover from './InfoPopover';
import { normalizeAnalyticsEntityTitleCase } from '../analyticsTextUtils';

const useStyles = makeStyles(({ tabColor }) => ({
  tableContainer: {
    height: '100%',
    maxHeight: '100%',
    overflow: 'auto',
    border: `1px solid ${ANALYTICS_CARD_BORDER_SOFT}`,
    borderRadius: '8px',
    background: ANALYTICS_SURFACE,
    flex: '1 1 auto',
    minHeight: 0,
    '& .MuiTableContainer-root': {
      height: '100%',
      maxHeight: '100%',
      overflow: 'auto',
    },
  },
  tableBody: {
    flex: '1 1 auto',
    minHeight: '260px',
    height: '100%',
    padding: '0 16px 16px',
    boxSizing: 'border-box',
    display: 'flex',
    overflow: 'hidden',
  },
  tableBodyExpanded: {
    minHeight: 0,
    paddingBottom: 0,
  },
  expandedContent: {
    width: '100%',
    height: '100%',
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  text: {
    paddingLeft: '5%',
    paddingTop: '1vh',
    color: '#ffffff',
    fontSize: '100%',
  },
  gridItem: {
    position: 'relative',
    width: '100%',
    display: 'flex',
  },
  card: {
    border: `1px solid ${ANALYTICS_CARD_BORDER}`,
    borderRadius: '10px',
    boxShadow: ANALYTICS_CARD_SHADOW,
    background: ANALYTICS_SURFACE,
    boxSizing: 'border-box',
    minHeight: '420px',
    height: '420px',
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    overflow: 'visible',
  },
}));

const GLOBAL_SCOPE = (() => {
  if (typeof globalThis === 'object') {
    return globalThis;
  }
  if (typeof document === 'object') {
    return document.defaultView;
  }
  return undefined;
})();
const INFO_DEBUG_QUERY_KEYS = new Set(['all-media-interactions', 'media-interactions-in-progress']);
const shouldLogInfoDebug = (queryKey) => process.env.NODE_ENV !== 'production' && INFO_DEBUG_QUERY_KEYS.has(queryKey);
const logTableInfoDebug = (queryKey, message, details) => {
  if (!shouldLogInfoDebug(queryKey)) {
    return;
  }
  console.log(message, { queryKey, ...details });
};

const resolveTableInfoCopy = ({
  subTitle,
  scope,
  infoTitle,
  infoBody,
  forceRestoredPopover,
  resolvedQueryKey,
  restoredInfo,
}) => {
  const normalizedSubTitle = normalizeAnalyticsEntityTitleCase(subTitle);
  const normalizedScope = normalizeAnalyticsEntityTitleCase(
    typeof scope === 'string' ? scope.trim().replace(/^included:\s*/i, '') : ''
  );
  const infoItems = Array.isArray(infoBody)
    ? infoBody
        .filter((item) => typeof item === 'string' && item.trim())
        .map((item) => normalizeAnalyticsEntityTitleCase(item))
    : [];
  const fallbackScope = forceRestoredPopover
    ? normalizeAnalyticsEntityTitleCase(restoredInfo[resolvedQueryKey]?.scope ?? '')
    : '';
  const fallbackItems = forceRestoredPopover
    ? (restoredInfo[resolvedQueryKey]?.items ?? []).map((item) => normalizeAnalyticsEntityTitleCase(item))
    : [];
  const resolvedScope = normalizedScope || fallbackScope;
  const resolvedInfoItems = infoItems.length > 0 ? infoItems : fallbackItems;
  const hasInfoBody = resolvedInfoItems.length > 0;
  const hasInfo = Boolean(resolvedScope || resolvedInfoItems.length > 0);
  const infoHeading = normalizeAnalyticsEntityTitleCase(
    infoTitle || (forceRestoredPopover ? restoredInfo[resolvedQueryKey]?.heading : '') || 'How this chart is counted'
  );

  return {
    normalizedSubTitle,
    resolvedScope,
    resolvedInfoItems,
    hasInfoBody,
    hasInfo,
    infoHeading,
  };
};

function TableComponent({ tableData, query }) {
  //component for rendering the tables at bottom of analytics page
  const { pathname } = useLocation();

  const theme = useTheme();
  const classes = useStyles({ tabColor: getTabColor(pathname, theme) });
  const { columns, dropdownTable, networkRequest, queryKey, subTitle, scope, infoTitle, infoBody, badges } = tableData;
  const tableQueryKey = tableData?.queryKey;
  const resolvedQueryKey = tableQueryKey || queryKey || tableData.title;
  const isInfoDebugEnabled = shouldLogInfoDebug(resolvedQueryKey);
  const forceInfo = ['all-media-interactions', 'media-interactions-in-progress'].includes(tableQueryKey);
  const gridLg = Number.isFinite(tableData?.gridLg) ? tableData.gridLg : 4;
  const gridSm = Number.isFinite(tableData?.gridSm) ? tableData.gridSm : 6;
  const forceRestoredPopover = forceInfo;
  const defaultRestoredInfo = {
    'all-media-interactions': {
      heading: 'How this chart is counted',
      scope: 'Media Interactions in your selected date range and filters',
      items: [
        'This is the full list behind the charts for this view',
        'Uses your selected filters (Status, Type, Outlet, Department, Campaigns, Units, Team Members)',
      ],
    },
    'media-interactions-in-progress': {
      heading: 'How this chart is counted',
      scope: 'Media Interactions currently marked In Progress in your selected date range and filters',
      items: [
        'This is an action list for follow-up work',
        'Uses your selected filters (Type, Outlet, Department, Campaigns, Units, Team Members)',
      ],
    },
  };

  const { normalizedSubTitle, resolvedScope, resolvedInfoItems, hasInfoBody, hasInfo, infoHeading } =
    resolveTableInfoCopy({
      subTitle,
      scope,
      infoTitle,
      infoBody,
      forceRestoredPopover,
      resolvedQueryKey,
      restoredInfo: defaultRestoredInfo,
    });
  const shouldRenderInfo = forceInfo || hasInfoBody || hasInfo;

  const [open, setOpen] = useState(false);
  const [infoAnchorEl, setInfoAnchorEl] = useState(null);
  const infoPopoverInstanceIdRef = useRef(
    `analytics-info-popover-${resolvedQueryKey}-${Math.random().toString(36).slice(2)}`
  );
  const lastInfoOpenAtRef = useRef(0);

  const buttonHandler = () => {
    setOpen(true);
  };
  const openInfoHandler = (event) => {
    event.stopPropagation();
    const clickedEl = event.currentTarget;
    logTableInfoDebug(resolvedQueryKey, '[CustomAnalytics Table info click]', {
      instanceId: infoPopoverInstanceIdRef.current,
      clickedTag: clickedEl?.tagName,
    });
    // Toggle if clicking the same icon again.
    if (infoAnchorEl === clickedEl) {
      setInfoAnchorEl(null);
      logTableInfoDebug(resolvedQueryKey, '[CustomAnalytics Table info close]', {
        reason: 'toggle-same-icon',
      });
      return;
    }
    // Notify sibling analytics cards so only one popover stays open.
    GLOBAL_SCOPE?.dispatchEvent?.(
      new CustomEvent('custom-analytics-info-open', {
        detail: { instanceId: infoPopoverInstanceIdRef.current },
      })
    );
    lastInfoOpenAtRef.current = Date.now();
    setInfoAnchorEl(clickedEl);
  };
  const closeInfoHandler = useCallback(
    (reason = 'unknown') => {
      const elapsed = Date.now() - lastInfoOpenAtRef.current;
      // Prevent same-click dismissal from immediately closing newly opened popovers.
      const isImmediateDismissReason = reason === 'click-away' || reason === 'backdropClick';
      const shouldSkipClose = isImmediateDismissReason && elapsed >= 0 && elapsed < 150;
      if (shouldSkipClose) {
        logTableInfoDebug(resolvedQueryKey, '[CustomAnalytics Table info close skipped]', {
          reason,
          elapsedMs: elapsed,
        });
        return;
      }
      logTableInfoDebug(resolvedQueryKey, '[CustomAnalytics Table info close]', {
        reason,
        elapsedMs: elapsed,
      });
      setInfoAnchorEl(null);
    },
    [resolvedQueryKey]
  );
  const openInfoPopover = Boolean(infoAnchorEl);

  useEffect(() => {
    const closeWhenAnotherCardOpens = (event) => {
      const openedInstanceId = event?.detail?.instanceId;
      // Ignore malformed events and this card's own open event.
      if (!openedInstanceId || openedInstanceId === infoPopoverInstanceIdRef.current) {
        return;
      }
      logTableInfoDebug(resolvedQueryKey, '[CustomAnalytics Table info event-close]', {
        thisInstanceId: infoPopoverInstanceIdRef.current,
        openedInstanceId,
      });
      closeInfoHandler('event-bus-other-card-opened');
    };

    GLOBAL_SCOPE?.addEventListener?.('custom-analytics-info-open', closeWhenAnotherCardOpens);
    return () => {
      GLOBAL_SCOPE?.removeEventListener?.('custom-analytics-info-open', closeWhenAnotherCardOpens);
    };
  }, [closeInfoHandler, resolvedQueryKey]);

  const tableQuery = useQuery({
    queryKey: ['analytics-table', resolvedQueryKey, query],
    queryFn: async () => {
      const result = await networkRequest(query);
      return result?.data ? result.data : result;
    },
    enabled: Boolean(query),
    staleTime: 2 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: 1,
  });

  const issuesState = tableQuery.isLoading ? 'loading' : tableQuery.data ?? 'loading';

  if (isInfoDebugEnabled) {
    console.log('[CustomAnalytics Table info debug]', {
      queryKeyFromTableData: tableQueryKey,
      queryKeyFromProp: queryKey,
      queryKey: resolvedQueryKey,
      hasInfo,
      hasInfoBody,
      shouldRenderInfo,
      hasScope: Boolean(resolvedScope),
      infoBodyCount: resolvedInfoItems.length,
      headerWillRenderInfoIcon: shouldRenderInfo,
      hasAnchorEl: Boolean(infoAnchorEl),
      isPopoverOpen: openInfoPopover,
      renderState: {
        tableQueryIsLoading: tableQuery.isLoading,
        dropdownTableIsLoading: dropdownTable === 'loading',
      },
    });
  }

  useEffect(() => {
    if (isInfoDebugEnabled) {
      console.log('[CustomAnalytics Table info anchor state]', {
        queryKey: resolvedQueryKey,
        instanceId: infoPopoverInstanceIdRef.current,
        hasAnchorEl: Boolean(infoAnchorEl),
      });
    }
  }, [infoAnchorEl, isInfoDebugEnabled, resolvedQueryKey]);

  const headerActions = [
    ...(shouldRenderInfo
      ? [
          {
            key: 'info',
            tooltip: 'How this chart is counted',
            ariaLabel: 'How this chart is counted',
            onClick: openInfoHandler,
            icon: <InfoOutlinedIcon />,
          },
        ]
      : []),
    {
      key: 'expand',
      tooltip: 'Expand table',
      ariaLabel: 'Expand table',
      onClick: buttonHandler,
      icon: <FullscreenIcon />,
    },
  ];

  const Container = ({ children }) => {
    const expandedDisplay = <div className={classes.expandedContent}>{children}</div>;

    return (
      <Grid item className={classes.gridItem} sm={gridSm} lg={gridLg}>
        <EnlargedChartModal open={open} onClose={() => setOpen(false)} display={expandedDisplay}></EnlargedChartModal>
        <Card className={classes.card}>{children}</Card>
      </Grid>
    );
  };
  const renderCard = (body) => (
    <Container>
      <AnalyticsCardHeader
        title={tableData.title}
        subTitle={normalizedSubTitle}
        badges={badges}
        actions={headerActions}
      />
      {shouldRenderInfo && (
        <InfoPopover
          open={openInfoPopover}
          anchorEl={infoAnchorEl}
          onClose={closeInfoHandler}
          heading={infoHeading}
          scope={resolvedScope}
          items={resolvedInfoItems}
        />
      )}
      {body}
    </Container>
  );

  if (tableQuery.isError) {
    return <ErrorComponent message={tableQuery.error?.message ?? 'Failed to load table data.'}></ErrorComponent>;
  }
  if (issuesState.error === 1) {
    return <ErrorComponent message={issuesState.message}></ErrorComponent>;
  }
  if (issuesState !== 'loading' && dropdownTable !== 'loading') {
    return renderCard(
      <div className={`${classes.tableBody} ${open ? classes.tableBodyExpanded : ''}`}>
        <div className={classes.tableContainer}>
          <FilteredTable type={tableData.type} issues={issuesState} cols={columns} dropdownTable={dropdownTable} />
        </div>
      </div>
    );
  }

  return renderCard(
    <div>
      <Typography>Waiting for data</Typography>
      <Spinner animation="border" />
    </div>
  );
}

TableComponent.propTypes = {
  tableData: PropTypes.shape({
    title: PropTypes.string.isRequired,
    type: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    columns: PropTypes.array,
    dropdownTable: PropTypes.any,
    networkRequest: PropTypes.func.isRequired,
    queryKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    subTitle: PropTypes.string,
    scope: PropTypes.string,
    infoTitle: PropTypes.string,
    infoBody: PropTypes.arrayOf(PropTypes.string),
    badges: PropTypes.arrayOf(
      PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({
          label: PropTypes.string,
          variant: PropTypes.string,
          tooltip: PropTypes.string,
        }),
      ])
    ),
    gridLg: PropTypes.number,
    gridSm: PropTypes.number,
  }).isRequired,
  query: PropTypes.string,
};

export default TableComponent;
